import React, { useState, useEffect } from 'react';
import './sidebar.css';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faBarsStaggered, faChevronDown, faChevronUp, faCalendarAlt, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { faHandshake, faCirclePlay } from '@fortawesome/free-regular-svg-icons';
import { useUsers } from '../../context/usersContext';
import Logo from './../../assets/brasao_sejus.png';
import HomeIcon from '../../assets/icons/home.svg';
import Escudo from '../../assets/icons/escudo.svg';
import Usuario from '../../assets/icons/userplus.svg';
import Coracao from '../../assets/icons/coracao.svg';
import Calendario from '../../assets/icons/calendary.svg';
import Lapis from '../../assets/icons/lapis.svg';
import Recicle from '../../assets/icons/recicle.svg';
import OlhoFechado from '../../assets/icons/olhofechado.svg';
import File from '../../assets/icons/file.svg';
import Pasta from '../../assets/icons/pasta.svg';
import BalaoFala from '../../assets/icons/balaofala.svg';
import Lupa from '../../assets/icons/lupa.svg';
import Engrenagem from '../../assets/icons/engrenagem.svg';
import VisibleAccess from '../../permissions/VisibleAccess';

// Perfis externos somente-leitura (acesso restrito a /internos + tela de detalhes externa).
// Ao liberar um novo perfil externo com esse mesmo comportamento, acrescentar o grupo aqui.
const EXTERNAL_PROFILE_GROUPS = ['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI'];

function Sidebar() {
  const { userInfo, effectiveUnitId } = useUsers();
  const location = useLocation();
  // Guarda defensiva para evitar erro quando userInfo ou groups vier vazio.
  const userGroups = Array.isArray(userInfo?.groups) ? userInfo.groups : [];
  const isAdvogado = userGroups.includes('ADVOGADO(A)');
  const isExternalProfile = EXTERNAL_PROFILE_GROUPS.some((group) => userGroups.includes(group));
  // Relatórios liberado só pro perfil externo DEFENSORIA, não pros demais (TJPI, Polícia, MPPI).
  const isDefensoria = userGroups.includes('DEFENSORIA');
  // Visitantes (Todos Visitantes + detalhes, só leitura) liberado só pro perfil externo Polícia.
  const isPolicia = userGroups.includes('POLÍCIA MILITAR / POLÍCIA CIVIL');

  const [isOpen, setIsOpen] = useState();

  const getWhatsappUrl = () => {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
    return `https://wa.me/5511913386582?text=${encodeURIComponent(`${greeting}, suporte SIGEPEN`)}`;
  };
  const [isMobile, setIsMobile] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [activeHealthItem, setActiveHealthItem] = useState(null);
  const [activeNursingItem, setActiveNursingItem] = useState(null);
  const [activeClinicItem, setActiveClinicItem] = useState(null);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const resetHealthState = () => {
    setActiveHealthItem(null);
    setActiveNursingItem(null);
    // setActiveClinicItem(null);
  };

  const toggleItem = (itemId) => {
    if (activeItem === itemId) {
      setActiveItem(null);
      if (itemId === 3) {
        resetHealthState();
      }
    } else {
      setActiveItem(itemId);
      if (itemId !== 3) {
        resetHealthState();
      }
    }
  };

  const toggleHealthItem = (itemId) => {
    if (activeHealthItem === itemId) {
      setActiveHealthItem(null);
      setActiveNursingItem(null);
      setActiveClinicItem(null);
    } else {
      setActiveHealthItem(itemId);
      setActiveNursingItem(null);
      setActiveClinicItem(null);
    }
  };

  const toggleNursingItem = (itemId) => {
    if (activeNursingItem === itemId) {
      setActiveNursingItem(null);
      setActiveClinicItem(null);
    } else {
      setActiveNursingItem(itemId);
      setActiveClinicItem(null);
    }
  };

  const toggleClinicItem = (itemId) => {
    if (activeClinicItem === itemId) {
      setActiveClinicItem(null);
    } else {
      setActiveClinicItem(itemId);
    }
  };

  // Recolhe a sidebar em telas menores que 800px ao carregar e ao redimensionar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 800;
      setIsMobile(mobile);

      if (mobile) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    // Executa a função de redimensionamento ao carregar o componente
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Mantém abertos os menus conforme a rota atual
  useEffect(() => {
    const path = location.pathname;

    const healthPaths = [
      '/admissao', '/evolucao', '/demanda-espontanea', '/vacinacao', '/doencas-cronicas',
      '/tuberculose', '/hanseniase', '/testes', '/saude-administrativo', '/assistencia-farmaceutica',
      '/encaminhamento', '/consulta-interna', '/exames', '/nutricao', '/psicologia', '/odontologia',
      '/medicacao'
    ];

    const nursingPaths = [
      '/admissao', '/evolucao', '/demanda-espontanea', '/vacinacao', '/doencas-cronicas',
      '/tuberculose', '/hanseniase', '/testes', '/medicacao'
    ];

    const infectoPaths = ['/tuberculose', '/hanseniase', '/testes'];

    if (healthPaths.includes(path)) {
      setActiveItem(3);
      if (nursingPaths.includes(path)) {
        setActiveHealthItem('enfermagem');
        setActiveNursingItem('assistencia-clinica');
        setActiveClinicItem(infectoPaths.includes(path) ? 'infecto-prioritarias' : null);
      } else {
        setActiveHealthItem(null);
        setActiveNursingItem(null);
        setActiveClinicItem(null);
      }
      return;
    }

    if (path.startsWith('/movimentacao') || path === '/cadastro-movimentacao' || path === '/sacolao') {
      setActiveItem(2);
      resetHealthState();
      return;
    }

    if (path.startsWith('/visitantes') || path === '/cadastro-visitante-sistema') {
      setActiveItem(5);
      resetHealthState();
      return;
    }

    if (path.startsWith('/educacao') || path.startsWith('/cadastros-edu') || path.startsWith('/escolas') || path.startsWith('/professores') || path.startsWith('/alunos') || path.startsWith('/cursos') || path.startsWith('/disciplinas') || path.startsWith('/turmas') || path.startsWith('/acervo-educacao')) {
      setActiveItem(4);
      resetHealthState();
      return;
    }

    if (path.startsWith('/internos') || path.startsWith('/liberacao')) {
      setActiveItem(1);
      resetHealthState();
      return;
    }

    // Rota não mapeada para menus com dropdown
    resetHealthState();
    setActiveItem(null);
  }, [location.pathname]);

  // Renderizacao da  rota por unidade logada
  const renderUnitLink = () => {
    if (effectiveUnitId !== null && effectiveUnitId !== undefined && String(effectiveUnitId) !== '') {
      return (
        <Link to={`/unidades/${effectiveUnitId}`}>
          <img src={Escudo} alt="Icon" className='i' />
          <span className="links_name">Unidade</span>
        </Link>
      );
    } else {
      return (
        <Link to="/unidades">
          <img src={Escudo} alt="Icon" className='i' />
          <span className="links_name">Unidades</span>
        </Link>
      );
    }
  };

  // Item "Meus Dados" reaproveitado em todos os perfis (staff geral, advogado e perfis externos) —
  // troca da própria senha deve ficar acessível pra qualquer usuário autenticado.
  const meusDadosMenuItem = (
    <li>
      <Link to='/usuario'>
        <img src={Engrenagem} alt='Incone' className='i' />
        <span className="links_name">Meus Dados</span>
        <span className="tooltip">Meus Dados</span>
      </Link>
    </li>
  );

  // Item "Internos" reaproveitado tanto no menu completo quanto no menu de perfis externos —
  // "Buscar" é livre pra quem enxerga este item; os demais sub-itens já são protegidos
  // individualmente por VisibleAccess (perfis externos nunca entram nesses grupos).
  const internosMenuItem = (
    <li>
      <div onClick={() => toggleItem(1)}>
        <img src={Usuario} alt="Icon" className='i' />
        <span className="links_name">Internos</span>
        <FontAwesomeIcon
          icon={activeItem === 1 ? faChevronUp : faChevronDown}
          id='hide-icon'
          className='i'
        />
      </div>
      <span className="tooltip">Internos</span>
      {activeItem === 1 && (
        <ul className='ul-options'>
          <li>
            <Link className='a-option' to="/internos">Buscar</Link>
          </li>

          <VisibleAccess compareField="groups" compareValues={["PLANTONISTA", "ADMINISTRATIVO", "DISCIPLINA"]}>
            <li>
              <Link className='a-option' to="/transferencia-cela">Mudança de Cela</Link>
            </li>
          </VisibleAccess>
          <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "DISCIPLINA", "GERENTE DE UNIDADE", "DIRETORES", "DUAP", "MONITORAMENTO"]}>
            <li>
              <Link className='a-option' to="/transferencia-unidade">Transferência</Link>
            </li>
          </VisibleAccess>
          <VisibleAccess compareField="groups" compareValues={["PLANTONISTA", "ADMINISTRATIVO", "DISCIPLINA", "MONITORAMENTO"]}>
            <li>
              <Link className='a-option' to="/liberacao">Registrar Saída</Link>
            </li>
          </VisibleAccess>
        </ul>
      )}
    </li>
  );

  return (
    <>
      {isMobile && isOpen && (
        <button
          type="button"
          className="mobile-sidebar-trigger"
          onClick={toggleSidebar}
          aria-label="Abrir menu"
          title="Abrir menu"
        >
          <FontAwesomeIcon icon={faBarsStaggered} />
        </button>
      )}

      {isMobile && !isOpen && (
        <div className="sidebar-backdrop" onClick={toggleSidebar} />
      )}

      <div className={`sidebar ${isOpen ? '' : 'open'} ${isMobile ? 'mobile' : ''}`}>
        <div className="logo-details">
          <img src={Logo} alt='Logo' />
          <FontAwesomeIcon
            icon={isOpen ? faBars : faBarsStaggered}
            onClick={toggleSidebar}
            id="btn"
          />
        </div>

        <ul className="nav-list">

          {isAdvogado ? ( // ADVOGADO(A)
            <>
              <li>
                <Link to="/advogado">
                  <FontAwesomeIcon icon={faCalendarAlt} className='i' />
                  <span className="links_name">Minha Agenda</span>
                </Link>
                <span className="tooltip">Minha Agenda</span>
              </li>
              {/* <li>
              <Link to="/agendamento">
                <FontAwesomeIcon icon={faCalendarPlus} className='i' />
                <span className="links_name">Criar novo</span>
              </Link>
              <span className="tooltip">Criar novo</span>
            </li> */}
              {meusDadosMenuItem}
            </>
          ) : isExternalProfile ? ( // Perfis externos somente-leitura
            <>
              {internosMenuItem}
              {isPolicia && (
                <li>
                  <Link to="/outros-visitantes">
                    <img src={OlhoFechado} alt='Incone' className='i' />
                    <span className="links_name">Visitantes</span>
                    <span className="tooltip">Visitantes</span>
                  </Link>
                </li>
              )}
              {isDefensoria && (
                <li>
                  <Link to="/relatorios">
                    <img src={File} alt='Incone' className='i' />
                    <span className="links_name">Relatórios</span>
                    <span className="tooltip">Relatórios</span>
                  </Link>
                </li>
              )}
              {meusDadosMenuItem}
            </>
          ) : (
            <>
              <li>
                <Link to="/">
                  <img src={HomeIcon} alt="Home Icon" className='i' />
                  <span className="links_name">Home</span>
                </Link>
                <span className="tooltip">Home</span>
              </li>

              {/* internos */}
              {internosMenuItem}

              <VisibleAccess compareField="groups" compareValues={["GERENTE DE UNIDADE", "DIRETORES", "ADMINISTRATIVO"]}>
                <li>
                  {renderUnitLink()}
                  <span className="tooltip">Unidades</span>
                </li>
              </VisibleAccess>

              <VisibleAccess compareField="groups" compareValues={["SERVIÇO SOCIAL"]}>
                <li>
                  <Link className='a-option' to="/servico-social">
                    <img src={BalaoFala} alt='Incone' className='i' />
                    <span className="links_name">Serviço social</span>
                    <span className="tooltip">Serviço social</span>
                  </Link>
                </li>
              </VisibleAccess>

              {/* Saúde GERAL*/}
              <VisibleAccess compareField="groups" compareValues={["ENFERMAGEM", "MEDICINA", "PSICOLOGIA", "DENTISTA", "NUTRIÇÃO"]}>
                <li>
                  <div onClick={() => toggleItem(3)}>
                    <img src={Coracao} alt="Icon" className='i' />
                    <span className="links_name">Saúde Geral</span>
                    <FontAwesomeIcon
                      icon={activeItem === 3 ? faChevronUp : faChevronDown}
                      id='hide-icon'
                      className='i'
                    />
                  </div>
                  <span className="tooltip">Saúde Geral</span>
                  {activeItem === 3 && (
                    <ul className='ul-options'>
                      {/* <VisibleAccess compareField="groups" compareValues={["ENFERMAGEM", "MEDICINA"]}>
                      <li>
                        <Link className='a-option' to="/consulta-interna">
                          <span className="links_name">Médico</span>
                        </Link>
                      </li>
                    </VisibleAccess> */}

                      <VisibleAccess compareField="groups" compareValues={["ENFERMAGEM", "MEDICINA"]}>
                        <li>
                          <div onClick={() => toggleHealthItem('enfermagem')}>
                            <span className="links_name">Enfermagem</span>
                            <FontAwesomeIcon
                              icon={activeHealthItem === 'enfermagem' ? faChevronUp : faChevronDown}
                              id='hide-icon'
                              className='i'
                            />
                          </div>
                          {activeHealthItem === 'enfermagem' && (
                            <ul className='ul-options'>
                              <li>
                                <div onClick={() => toggleNursingItem('assistencia-clinica')}>
                                  <span className="links_name">Assistência clínica</span>
                                  <FontAwesomeIcon
                                    icon={activeNursingItem === 'assistencia-clinica' ? faChevronUp : faChevronDown}
                                    id='hide-icon'
                                    className='i'
                                  />
                                </div>
                                {activeNursingItem === 'assistencia-clinica' && (
                                  <ul className='ul-options'>
                                    <li>
                                      <Link className='a-option' to="/admissao">Admissão</Link>
                                    </li>
                                    <li>
                                      <Link className='a-option' to="/evolucao">Evolução</Link>
                                    </li>
                                    <li>
                                      <Link className='a-option' to="/testes">Testagens</Link>
                                    </li>
                                    <li>
                                    <div onClick={() => toggleClinicItem('infecto-prioritarias')}>
                                      <span className="links_name">Doenças prioritárias</span>
                                      <FontAwesomeIcon
                                        icon={activeClinicItem === 'infecto-prioritarias' ? faChevronUp : faChevronDown}
                                        id='hide-icon'
                                        className='i'
                                      />
                                    </div>
                                    {activeClinicItem === 'infecto-prioritarias' && (
                                      <ul className='ul-options'>
                                        <li>
                                          <Link className='a-option' to="/tuberculose">Tuberculose</Link>
                                        </li>
                                        <li>
                                          <Link className='a-option' to="/hanseniase">Hanseníase</Link>
                                        </li>
                                      </ul>
                                    )}
                                  </li>
                                    <li>
                                      <Link className='a-option' to="/demanda-espontanea">Demanda espontânea</Link>
                                    </li>
                                    <li>
                                      <Link className='a-option' to="/vacinacao">Vacinas</Link>
                                    </li>
                                    <li>
                                      <Link className='a-option' to="/medicacao">Medicação</Link>
                                    </li>
                                  </ul>
                                )}
                              </li>
                            </ul>
                          )}
                        </li>
                      </VisibleAccess>

                      {/* <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "ENFERMAGEM", "MEDICINA"]}>
                      <li>
                        <Link className='a-option' to="/saude-administrativo">
                          <span className="links_name">Administrativo</span>
                        </Link>
                      </li>
                    </VisibleAccess> */}

                      <VisibleAccess compareField="groups" compareValues={["DENTISTA"]}>
                        <li>
                          <Link className='a-option' to="/odontologia">
                            <span className="links_name">Odontologia</span>
                          </Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["NUTRIÇÃO"]}>
                        <li>
                          <Link className='a-option' to="/nutricao">
                            <span className="links_name">Nutrição</span>
                          </Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["PSICOLOGIA"]}>
                        <li>
                          <Link className='a-option' to="/psicologia">
                            <span className="links_name">Psicologia</span>
                          </Link>
                        </li>
                      </VisibleAccess>
                      {/* <VisibleAccess compareField="groups" compareValues={["ENFERMAGEM", "MEDICINA", "ADMINISTRATIVO"]}>
                      <li>
                        <Link className='a-option' to="/assistencia-farmaceutica">
                          <span className="links_name">Assistência farmacêutica</span>
                        </Link>
                      </li>
                    </VisibleAccess> */}
                    </ul>
                  )}
                </li>
              </VisibleAccess>

              {/* Trabalho */}
              <VisibleAccess compareField="groups" compareValues={["SERVIÇO SOCIAL", "ADMINISTRATIVO", "DISCIPLINA"]}>
                <li>
                  <Link className='a-option' to="/trabalho">
                    <img src={Pasta} alt='Incone' className='i' />
                    <span className="links_name">Trabalho</span>
                    <span className="tooltip">Trabalho</span>
                  </Link>
                </li>
              </VisibleAccess>

              {/* Educação */}
              <VisibleAccess compareField="groups" compareValues={["EDUCAÇÃO"]}>
                <li>
                  <div onClick={() => toggleItem(4)}>
                    <img src={Lapis} alt="Icon" className='i' />
                    <span className="links_name">Educação</span>
                    <FontAwesomeIcon
                      icon={activeItem === 4 ? faChevronUp : faChevronDown}
                      id='hide-icon'
                      className='i'
                    />
                  </div>
                  <span className="tooltip">Educação</span>
                  {activeItem === 4 && (
                    <ul className='ul-options'>
                      <VisibleAccess compareField="groups" compareValues={["EDUCAÇÃO",]}>
                        <li>
                          <Link className='a-option' to="/educacao">Geral</Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["EDUCAÇÃO"]}>
                        <li>
                          <Link className='a-option' to="/cadastros-edu">Cadastros</Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["EDUCAÇÃO"]}>
                        <li>
                          <Link className='a-option' to="/painel-seduc">SEDUC</Link>
                        </li>
                      </VisibleAccess>

                    </ul>
                  )}
                </li>
              </VisibleAccess>

              {/* MOVIMENTAÇÃO */}
              <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "DISCIPLINA", "PLANTONISTA", "GERENTE DE UNIDADE", "DIRETORES", "DUAP", "SERVIÇO SOCIAL"]}>
                <li>
                  <div onClick={() => toggleItem(2)}>
                    <img src={Recicle} alt="Icon" className='i' />
                    <span className="links_name">Controle acesso</span>
                    <FontAwesomeIcon
                      icon={activeItem === 2 ? faChevronUp : faChevronDown}
                      id='hide-icon'
                      className='i'
                    />
                  </div>
                  <span className="tooltip">Controle acesso</span>
                  {activeItem === 2 && (
                    <ul className='ul-options'>
                      <li>
                        <Link className='a-option' to='/movimentacao'>Buscar</Link>
                      </li>
                      <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "DISCIPLINA", "PLANTONISTA"]}>
                        <li>
                          <Link className='a-option' to="/cadastro-movimentacao">Cadastrar</Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "DISCIPLINA", "PLANTONISTA"]}>
                        <li>
                          <Link className='a-option' to="/sacolao">Sacolão</Link>
                        </li>
                      </VisibleAccess>
                    </ul>
                  )}
                </li>
              </VisibleAccess>

              {/* visitantes */}
              <VisibleAccess compareField="groups" compareValues={["PLANTONISTA", "DISCIPLINA", "SERVIÇO SOCIAL", "ADMINISTRATIVO", "GERENTE DE UNIDADE", "DIRETORES", "DUAP"]}>
                <li>
                  <div onClick={() => toggleItem(5)}>
                    <img src={OlhoFechado} alt='Incone' className='i' />
                    <span className="links_name">Visitantes</span>
                    <FontAwesomeIcon
                      icon={activeItem === 5 ? faChevronUp : faChevronDown}
                      id='hide-icon'
                      className='i'
                    />
                  </div>
                  <span className="tooltip">Visitantes</span>
                  {activeItem === 5 && (
                    <ul className='ul-options'>
                      <VisibleAccess compareField="groups" compareValues={["SERVIÇO SOCIAL"]}>
                        <li>
                          <Link className='a-option' to="/visitantes">Familiar</Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["SERVIÇO SOCIAL", "ADMINISTRATIVO"]}>
                        <li>
                          <Link className='a-option' to="/cadastro-visitante-sistema">Cadastro</Link>
                        </li>
                      </VisibleAccess>
                      <VisibleAccess compareField="groups" compareValues={["SERVIÇO SOCIAL", "ADMINISTRATIVO", "PLANTONISTA", "DISCIPLINA", "DUAP"]}>
                        <li>
                          <Link className='a-option' to="/outros-visitantes">Todos Visitantes</Link>
                        </li>
                      </VisibleAccess>
                      {/* <VisibleAccess compareField="groups" compareValues={["SERVIÇO SOCIAL"]}>
                      <li>
                        <Link className='a-option' to="/autorizacao-visita-servico-social">Altorização</Link>
                      </li>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={["DISCIPLINA"]}>
                      <li>
                        <Link className='a-option' to="/autorizacao-visita-disciplina">Altorização</Link>
                      </li>
                    </VisibleAccess> */}
                    </ul>
                  )}
                </li>
              </VisibleAccess>

              {/* AGENDAMENTO */}
              <VisibleAccess compareField="groups" compareValues={["GERENTE DE UNIDADE", "DIRETORES", "ADMINISTRATIVO", "PLANTONISTA", "DISCIPLINA", "SERVIÇO SOCIAL", "PSICOLOGIA", "DENTISTA", "NUTRIÇÃO", "ENFERMAGEM", "MEDICINA", "EDUCAÇÃO", "ADVOGADO(A)", "MONITORAMENTO"]}>
                <li>
                  <Link to="/agendamento">
                    <img src={Calendario} alt='Incone' className='i' />
                    <span className="links_name">Agendamento</span>
                    <span className="tooltip">Agendamento</span>
                  </Link>
                </li>
              </VisibleAccess>

              {/* AUDIÊNCIAS */}
              <VisibleAccess compareField="groups" compareValues={["DUAP", "ADMINISTRATIVO", "GERENTE DE UNIDADE"]}>
                <li>
                  <Link to="/audiencias">
                    <FontAwesomeIcon icon={faHandshake} className='i i-fa' />
                    <span className="links_name">Audiências</span>
                    <span className="tooltip">Audiências</span>
                  </Link>
                </li>
              </VisibleAccess>

              {/* AGENDAMENTOS DE ADVOGADO(A)S */}
              <VisibleAccess compareField="groups" compareValues={["DUAP"]}>
                <li>
                  <Link to="/agendamentos-advogados">
                    <img src={Calendario} alt='Incone' className='i' />
                    <span className="links_name">Agendamento</span>
                    <span className="tooltip">Agendamento</span>
                  </Link>
                </li>
              </VisibleAccess>
              <li>
                <Link to="/relatorios">
                  <img src={File} alt='Incone' className='i' />
                  <span className="links_name">Relatórios</span>
                  <span className="tooltip">Relatórios</span>
                </Link>
              </li>

              <li>
                <Link to="/treinamentos">
                  <FontAwesomeIcon icon={faCirclePlay} className='i i-fa' />
                  <span className="links_name">Treinamentos</span>
                  <span className="tooltip">Treinamentos</span>
                </Link>
              </li>

              <li>
                <Link to="/paineis">
                  <FontAwesomeIcon icon={faChartBar} className='i' />
                  <span className="links_name">Painéis</span>
                  <span className="tooltip">Painéis</span>
                </Link>
              </li>

              {/* AIFRESCO */}
              <li>
                <Link to="/pesquisar-prontuario">
                  <img src={Lupa} alt='Incone' className='i' />
                  <span className="links_name">Pesquisar Prontuário</span>
                  <span className="tooltip">Pesquisar Prontuário</span>
                </Link>
              </li>

              {/* MEUS DADOS / PERFIL DO USUÁRIO */}
              {meusDadosMenuItem}
            </>
          )}
        </ul>
        <a
          className="support-footer-link"
          href={getWhatsappUrl()}
          target="_blank"
          rel="noreferrer"
          title="Contact support 9h as 12h"
        >
          <img src={BalaoFala} alt="Support" className="support-footer-icon" />
          <span className="support-footer-text">Contact support 9h as 12h</span>
        </a>
      </div>
    </>
  );
}


export default Sidebar;
